// src/pages/cadastros/Setores/NovoSetor.jsx
// (Mamute Fitness) UI rebatizado para "Academias".
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import { useIsMobile } from "../../../hooks/useIsMobile";
import GoogleMapsAutocomplete from "../../../components/GoogleMapsAutocomplete/GoogleMapsAutocomplete";

import { db } from "../../../config/firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { useAuth } from "../../../contexts/AuthContext";

/**
 * Mantém o contador em "contadores/setores_{grupoId}" para evitar mexer em rules/estrutura.
 * Apenas muda o prefixo do código visível para ACxxx.
 */
async function obterProximoCodigoSequencialSetor(grupoId) {
  const ref = doc(db, "contadores", `setores_${grupoId}`);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    // ✅ Regras exigem grupoId nos contadores (exceto os especiais).
    await setDoc(ref, { grupoId, setoresAtual: 1, updatedAt: serverTimestamp() });
    return 1;
  }

  const atual = snap.data()?.setoresAtual ?? 0;
  const proximo = atual + 1;

  // ✅ Mantém grupoId no contador para compatibilidade com rules.
  await updateDoc(ref, { grupoId, setoresAtual: proximo, updatedAt: serverTimestamp() });
  return proximo;
}

function NovoSetor() {
  const { tema } = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { usuario: usuarioLogado } = useAuth();

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const grupoId = usuarioLogado?.grupoId || null;

  const [empresas, setEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState("");

  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [contato, setContato] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [quantidadeEquipamentos, setQuantidadeEquipamentos] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [carregandoEmpresas, setCarregandoEmpresas] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [alerta, setAlerta] = useState({ tipo: "", msg: "" });

  const titulo = useMemo(() => "Nova Academia", []);

  useEffect(() => {
    async function carregarEmpresas() {
      if (!grupoId) {
        setCarregandoEmpresas(false);
        return;
      }

      setCarregandoEmpresas(true);

      try {
        const empresasRef = collection(db, "empresas");
        const q = query(empresasRef, where("grupoId", "==", grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));

        setEmpresas(lista);
      } catch (e) {
        console.error("Erro ao carregar clientes (empresas):", e);
        setAlerta({ tipo: "erro", msg: "Erro ao carregar clientes. Tente novamente." });
      } finally {
        setCarregandoEmpresas(false);
      }
    }

    carregarEmpresas();
  }, [grupoId]);

  async function onSubmit(e) {
    e.preventDefault();
    setAlerta({ tipo: "", msg: "" });

    if (!grupoId) {
      setAlerta({ tipo: "erro", msg: "Usuário sem grupoId. Verifique seu cadastro." });
      return;
    }

    if (!empresaSelecionada) {
      setAlerta({ tipo: "erro", msg: "Selecione o cliente." });
      return;
    }

    if (!nome.trim()) {
      setAlerta({ tipo: "erro", msg: "Informe o nome da academia." });
      return;
    }

    setSalvando(true);

    try {
      const empresaData = empresas.find((e) => e.id === empresaSelecionada) || null;

      const sequencial = await obterProximoCodigoSequencialSetor(grupoId);
      const novoCodigo = `AC${String(sequencial).padStart(3, "0")}`;

      const qtd = String(quantidadeEquipamentos || "").trim();
      const qtdNum = qtd ? Number(qtd) : null;

      await addDoc(collection(db, "setores"), {
        // escopo
        grupoId,
        empresaId: empresaSelecionada,
        empresaNome: empresaData?.nome || empresaData?.empresaNome || "",
        empresaCodigo: empresaData?.codigo || empresaData?.empresaCodigo || "",

        // dados da academia
        codigo: novoCodigo,
        codigoSequencial: sequencial,
        nome: nome.trim(),
        endereco: endereco.trim(),
        contato: contato.trim(),
        telefone: telefone.trim(),
        email: email.trim(),
        quantidadeEquipamentos: Number.isFinite(qtdNum) ? qtdNum : null,
        observacoes: observacoes.trim(),

        // auditoria
        criadoEm: serverTimestamp(),
        criadoPor: usuarioLogado?.uid || usuarioLogado?.id || null,
        updatedAt: serverTimestamp(),
      });

      setAlerta({ tipo: "ok", msg: `Academia criada com sucesso (${novoCodigo}).` });

      // mantém cliente selecionado para facilitar cadastros em lote
      setNome("");
      setEndereco("");
      setContato("");
      setTelefone("");
      setEmail("");
      setQuantidadeEquipamentos("");
      setObservacoes("");
    } catch (e) {
      console.error("Erro ao salvar academia:", e);
      setAlerta({ tipo: "erro", msg: "Erro ao salvar academia. Tente novamente." });
    } finally {
      setSalvando(false);
    }
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    marginTop: 4,
    borderRadius: 8,
    border: "1px solid #334155",
    boxSizing: "border-box",
  };

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div
        className={isMobile ? "m-card" : "pc-card"}
        style={{
          width: "100%",
          padding: isMobile ? 16 : "16px 40px",
          boxSizing: "border-box",
        }}
      >
        {!isMobile && (
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Cadastro de academia</h3>
              <p>Cadastre uma academia e vincule a um cliente (cadastro de Clientes).</p>
            </div>
          </header>
        )}

        {alerta?.msg && (
          <div
            style={{
              marginTop: isMobile ? 0 : 12,
              marginBottom: 12,
              padding: 8,
              borderRadius: 8,
              background: alerta.tipo === "ok" ? "#14532d" : "#7f1d1d",
              color: alerta.tipo === "ok" ? "#bbf7d0" : "#fecaca",
              fontSize: 14,
            }}
          >
            {alerta.msg}
          </div>
        )}

        <form
          onSubmit={onSubmit}
          style={{
            marginTop: isMobile ? 0 : 12,
            maxWidth: isMobile ? "100%" : 720,
            marginLeft: "auto",
            marginRight: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* CLIENTE */}
          <label>
            <span style={{ fontSize: 14 }}>Cliente</span>
            <select
              value={empresaSelecionada}
              onChange={(e) => setEmpresaSelecionada(e.target.value)}
              disabled={carregandoEmpresas}
              className={isMobile ? "m-select" : ""}
              style={{ ...inputStyle, color: "inherit" }}
            >
              <option value="">
                {carregandoEmpresas ? "Carregando..." : "Selecione o cliente"}
              </option>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nome || "(sem nome)"}
                </option>
              ))}
            </select>
          </label>

          {/* NOME */}
          <label>
            <span style={{ fontSize: 14 }}>Nome da academia</span>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className={isMobile ? "m-input" : ""}
              style={inputStyle}
            />
          </label>

          {/* ENDEREÇO */}
          <label>
            <span style={{ fontSize: 14 }}>Endereço</span>
            <GoogleMapsAutocomplete
              value={endereco}
              onChange={setEndereco}
              onPlaceSelected={(place) => setEndereco(place.address || "")}
              className={isMobile ? "m-input" : ""}
              inputStyle={inputStyle}
              grupoId={grupoId || ""}
              disabled={!grupoId}
            />
          </label>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 12,
            }}
          >
            {/* CONTATO */}
            <label>
              <span style={{ fontSize: 14 }}>Contato</span>
              <input
                type="text"
                value={contato}
                onChange={(e) => setContato(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={inputStyle}
              />
            </label>

            {/* TELEFONE */}
            <label>
              <span style={{ fontSize: 14 }}>Telefone</span>
              <input
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={inputStyle}
              />
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 12,
            }}
          >
            {/* EMAIL */}
            <label>
              <span style={{ fontSize: 14 }}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={inputStyle}
              />
            </label>

            {/* QTD EQUIP */}
            <label>
              <span style={{ fontSize: 14 }}>Quantidade de equipamentos</span>
              <input
                type="number"
                min="0"
                step="1"
                value={quantidadeEquipamentos}
                onChange={(e) => setQuantidadeEquipamentos(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={inputStyle}
              />
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 12,
            }}
          >
          </div>

          {/* OBS */}
          <label>
            <span style={{ fontSize: 14 }}>Observações</span>
            <textarea
              rows={4}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              className={isMobile ? "m-input" : ""}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </label>

          <div
            className={isMobile ? "m-form-actions" : ""}
            style={
              isMobile
                ? {}
                : {
                    marginTop: 16,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }
            }
          >
            <button
              type="submit"
              className={isMobile ? "m-btn-primary" : "pc-btn-primario"}
              disabled={salvando || carregandoEmpresas}
            >
              {salvando ? "Salvando..." : "Salvar academia"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/cadastros/setores")}
              className={isMobile ? "m-btn-secondary" : "pc-btn-secundario"}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </section>
  );

  return (
    <Layout
      tema={tema}
      titulo={titulo}
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
    >
      {conteudo}
    </Layout>
  );
}

export default NovoSetor;
